import {
    cn,
    isTerminalMessage,
    openPromoHub,
    PromoWrapper,
    syncPromoReactUIConfigurationStore,
    syncPromoReactUINavigationStore,
    syncPromoReactUIPromoBonusStore,
    syncPromoReactUISessionStore,
    syncReactUIJackpotStore,
    updateReactUIIsSoundMuted,
    useMessageActions,
} from '@amusnet/react-ui'
import { GameUIMessageType } from 'game-ui-types'
import { useAmuseBoostPromoWidgetPositions } from '@hooks/useAmuseBoostPromoWidgetPositions'
import useGetBetAmount from '@hooks/useGetBetAmount'
import { useComponentStatesStore } from '@stores/componentStatesStore'
import { useConfigurationStore } from '@stores/configurationStore'
import { useCountdownFSBonusStore } from '@stores/countdownFSBonusStore'
import { useFreeBuyBonusStore } from '@stores/freeBuyBonusStore'
import { useFreeSpinsBonusesStore } from '@stores/freeSpinsBonusesStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { useLuckySpinsStore } from '@stores/luckySpinsStore'
import { useNavigationStore } from '@stores/navigationStore'
import { usePlayerInfoStore } from '@stores/playerInfoStore'
import { useSpinButtonSectionStore } from '@stores/spinButtonSectionStore'
import React, { useEffect, useState } from 'react'
import { useMessageData } from '@hooks'
import { usePromoStore } from '@stores/promoStore'

interface PromoProps {
    stageScale: number
}

const Promo: React.FC<PromoProps> = ({ stageScale }) => {
    const setCurrentAvailablePromoWidgets = useComponentStatesStore((state) => state.setCurrentAvailablePromoWidgets)
    const currentAvailablePromoWidgets = useComponentStatesStore((state) => state.currentAvailablePromoWidgets)
    const closePromoSignal = usePromoStore((state) => state.closePromoSignal)

    // Report whether a promo widget is actually available/enabled (i.e. there are promos/bonuses)
    const isPromoWidgetAvailable = currentAvailablePromoWidgets.length > 0

    useEffect(() => {
        const promoWidgetStatusUpdatedMessage = {
            type: GameUIMessageType.PROMO_WIDGET_STATUS_UPDATED,
            payload: { isAvailable: isPromoWidgetAvailable },
        }

        window.postMessage(promoWidgetStatusUpdatedMessage, '*')
    }, [isPromoWidgetAvailable])

    const [shouldPromoMoveToTopLayer, setShouldPromoMoveToTopLayer] = useState(false)
    const isGambleActive = useSpinButtonSectionStore((state) => state.isGambleActive)

    const viewport = useConfigurationStore((state) => state.viewport)
    const configurationStoreState = {
        viewport,
        currency: useConfigurationStore((state) => state.currency),
        currencyHideCents: useConfigurationStore((state) => state.currencyHideCents),
        playerName: useConfigurationStore((state) => state.playerName),
        playerUid: useConfigurationStore((state) => state.playerUid),
        authToken: useConfigurationStore((state) => state.token),
        apiBaseUrl: useConfigurationStore((state) => state.apiBaseUrl),
        language: useConfigurationStore((state) => state.language),
        portalUid: useConfigurationStore((state) => state.portalUid),
        portalCode: useConfigurationStore((state) => state.portalCode),
        promoModes: useConfigurationStore((state) => state.promoModes),
        shouldHideJackpotWinnerNames: useConfigurationStore((state) => state.shouldHideJackpotWinnerNames),
    }

    useEffect(() => {
        syncPromoReactUIConfigurationStore(configurationStoreState)
    }, [configurationStoreState])

    const isSoundMuted = useConfigurationStore((state) => state.isSoundMuted)

    useEffect(() => {
        updateReactUIIsSoundMuted(isSoundMuted)
    }, [isSoundMuted])

    const betAmount = useGetBetAmount()
    const shouldBalanceSubtractOnly = useConfigurationStore((state) => state.shouldBalanceSubtractOnly)
    const balance = usePlayerInfoStore((state) => state.balance)
    const subtractedBalance = usePlayerInfoStore((state) => state.subtractedBalance)
    const displayedBalance = shouldBalanceSubtractOnly ? (subtractedBalance ?? balance) : balance

    useEffect(() => {
        syncPromoReactUISessionStore({ betAmount, balance: displayedBalance })
    }, [betAmount, displayedBalance])

    useMessageActions({
        showSystemMessage({ messageType }) {
            if (isTerminalMessage(messageType)) {
                syncPromoReactUISessionStore({ isSessionActive: false })
            }
        },
    })

    const isHomeDisabled = useNavigationStore((state) => state.isHomeDisabled)

    useEffect(() => {
        syncPromoReactUINavigationStore({ isHomeDisabled })
    }, [isHomeDisabled])

    const isFSInProgress = useFreeSpinsBonusesStore((state) => state.showRemainingSpinsBanner)
    const isCFSInProgress = useCountdownFSBonusStore((state) => state.showRemainingSpinsBanner)
    const isFBBInProgress = useFreeBuyBonusStore((state) => state.hasActivatedBonus)
    const isCurrentlyConsumingLS = useLuckySpinsStore((state) => state.showRemainingSpinsBanner)
    const areLSAwarded = useLuckySpinsStore((state) => state.areLSAwarded)

    // The bonus notifications disable their Play Now button while a bonus is already being played
    useEffect(() => {
        syncPromoReactUIPromoBonusStore({
            isFSInProgress,
            isLSInProgress: isCurrentlyConsumingLS,
            isCFSInProgress,
            isFBBInProgress,
            areLSAwarded,
        })
    }, [isFSInProgress, isCurrentlyConsumingLS, isCFSInProgress, isFBBInProgress, areLSAwarded])

    const jackpotPhase = useJackpotStore((state) => state.jackpotPhase)

    useEffect(() => {
        syncReactUIJackpotStore({ jackpotPhase })
    }, [jackpotPhase])

    useMessageData(({ command }) => {
        if (command === 'OPEN_TOURNAMENT_MENU') {
            // This case is for expo tournaments, when FS are exhausted
            openPromoHub('tournaments')
        }
    })

    const isLSAvailableInGame = useConfigurationStore((state) => state.hasLuckySpins)

    const widgetComponentDimensions = useConfigurationStore((s) => s.getComponentConfig('PromoWidget').dimensions)
    const widgetComponentStyles = useConfigurationStore((s) => s.getComponentConfig('PromoWidget').styles)
    const { promoWidgetPosition } = useAmuseBoostPromoWidgetPositions()

    const setConfiguration = useConfigurationStore((state) => state.setConfiguration)
    const shouldDisableSpaceKey = useConfigurationStore((state) => state.shouldDisableSpaceKey)
    const [initialShouldDisableSpaceKey] = useState(shouldDisableSpaceKey)

    const setShouldDisableBetShortcut = (shouldDisable: boolean) => {
        if (shouldDisable) {
            setConfiguration({ shouldDisableSpaceKey: shouldDisable })
        } else {
            setConfiguration({ shouldDisableSpaceKey: initialShouldDisableSpaceKey })
        }
    }

    const handleUpdateBalance = (newBalance: number) => {
        if (newBalance === balance) return

        window.postMessage({
            id: 'EventForGame',
            data: { command: 'balance', balance: newBalance },
        })
    }

    return (
        <PromoWrapper
            forceClosePromo={closePromoSignal}
            stageScale={stageScale}
            widgetComponentConfig={{
                styles: {
                    position: 'absolute',
                    ...widgetComponentDimensions,
                    ...promoWidgetPosition,
                },
                classNames: widgetComponentStyles,
            }}
            isLSAvailableInGame={isLSAvailableInGame}
            isCurrentlyConsumingLS={isCurrentlyConsumingLS}
            updateBalance={handleUpdateBalance}
            setShouldPromoMoveToTopLayer={setShouldPromoMoveToTopLayer}
            setCurrentAvailableWidgets={setCurrentAvailablePromoWidgets}
            setShouldDisableBetShortcut={setShouldDisableBetShortcut}
            className={cn(
                'absolute inset-0 w-full h-full pointer-events-none',
                shouldPromoMoveToTopLayer ? 'z-[1001]' : 'z-[201]',
                // hide when in gamble unless widget forces to be seen eg. when bomb explodes
                isGambleActive && !shouldPromoMoveToTopLayer && 'opacity-0'
            )}
        />
    )
}

export default Promo
